/**
 * DXF (AutoCAD R12 ASCII) exporter for PMMA laser cutting.
 *
 * Generates LWPOLYLINE entities for each character outline (outer + counters/holes).
 * Output is in millimetres, ready to import in LightBurn / RDWorks / Cura Engrave / Inkscape.
 *
 * For each segment chunk we sample the font shapes into closed polylines.
 */
import * as THREE from 'three';

const NEWLINE = '\r\n';

function header() {
  // Minimal R12 header (sufficient for laser cutters)
  return [
    '0', 'SECTION',
    '2', 'HEADER',
    '9', '$ACADVER', '1', 'AC1009',
    '9', '$INSUNITS', '70', '4', // 4 = millimetres
    '0', 'ENDSEC',
    '0', 'SECTION', '2', 'TABLES',
    '0', 'TABLE', '2', 'LAYER', '70', '2',
    // Two layers: CUT (outer + holes), ENGRAVE (optional info)
    '0', 'LAYER', '2', 'CUT', '70', '0', '62', '1', '6', 'CONTINUOUS',
    '0', 'LAYER', '2', 'ENGRAVE', '70', '0', '62', '5', '6', 'CONTINUOUS',
    '0', 'ENDTAB',
    '0', 'ENDSEC',
    '0', 'SECTION', '2', 'ENTITIES',
  ].join(NEWLINE) + NEWLINE;
}

function footer() {
  return [
    '0', 'ENDSEC',
    '0', 'EOF',
  ].join(NEWLINE) + NEWLINE;
}

// Build a LWPOLYLINE entity from an array of {x, y} points (closed).
function polylineEntity(points, layer = 'CUT') {
  if (!points || points.length < 2) return '';
  const closed = 1;
  const out = [];
  out.push('0', 'LWPOLYLINE');
  out.push('8', layer);
  out.push('100', 'AcDbEntity');
  out.push('100', 'AcDbPolyline');
  out.push('90', String(points.length));
  out.push('70', String(closed));
  points.forEach((p) => {
    out.push('10', p.x.toFixed(4));
    out.push('20', p.y.toFixed(4));
  });
  return out.join(NEWLINE) + NEWLINE;
}

function textEntity(x, y, height, value, layer = 'ENGRAVE') {
  const out = [];
  out.push('0', 'TEXT');
  out.push('8', layer);
  out.push('10', x.toFixed(4));
  out.push('20', y.toFixed(4));
  out.push('30', '0');
  out.push('40', height.toFixed(4));
  out.push('1', value);
  return out.join(NEWLINE) + NEWLINE;
}

/**
 * Build DXF content for a single text chunk at given mm height.
 * Returns DXF string and bounding box in mm.
 */
export function buildDxfFromFont(font, text, heightMm, opts = {}) {
  const shapes = font.generateShapes(text || ' ', heightMm);
  const divisions = opts.curveDivisions || 28;
  let dxf = header();
  const bb = new THREE.Box2();
  shapes.forEach((shape) => {
    // Outer contour
    const outerPts = shape.getPoints(divisions);
    if (outerPts.length >= 3) {
      dxf += polylineEntity(outerPts, 'CUT');
      outerPts.forEach((p) => bb.expandByPoint(p));
    }
    // Inner counters (O, A, e, g…) — also CUT layer (laser will cut them out)
    (shape.holes || []).forEach((h) => {
      const holePts = h.getPoints ? h.getPoints(divisions) : [];
      if (holePts.length >= 3) {
        dxf += polylineEntity(holePts, 'CUT');
      }
    });
  });
  // Optional label below the shape (engraved info)
  if (opts.label) {
    dxf += textEntity(bb.min.x, bb.min.y - 8, 4, opts.label, 'ENGRAVE');
  }
  dxf += footer();
  return { dxf, bbox: bb };
}

/**
 * Build a DXF file for every segment chunk of the front face (PMMA).
 * Returns array of { fileName, content } to add to the ZIP.
 */
export function buildAllFaceDxfFiles(font, segments, dimensions, splitByVolume) {
  const heightMm = dimensions.height;
  const out = [];
  for (let s = 0; s < segments.length; s++) {
    const seg = segments[s];
    const safe = (seg.text || `seg${s + 1}`).replace(/[^a-zA-Z0-9-_]/g, '_');
    const chunks = splitByVolume(seg.text);
    chunks.forEach((chunk, ci) => {
      console.log('[DXF] processing', s, ci, chunk);
      const idx = chunks.length > 1 ? `_part${ci + 1}of${chunks.length}` : '';
      const baseName = `seg${s + 1}_${safe}${idx}`;
      const { dxf } = buildDxfFromFont(font, chunk, heightMm, {
        label: `${seg.text} — Face PMMA — Ép. ${dimensions.acrylicThickness || 4} mm`,
      });
      console.log('[DXF] done', baseName, dxf.length, 'chars');
      out.push({
        fileName: `${baseName}_face.dxf`,
        content: dxf,
        segment: seg.text,
        chunk,
        material: seg.materials?.front || 'pmma',
        color: seg.colors?.front,
        thickness_mm: dimensions.acrylicThickness || 4,
        detached: !!seg.detached,
        detachedFolder: seg.detached ? `detached_signs/sign_${s + 1}_${safe}` : null,
      });
    });
  }
  return out;
}
